import React from 'react';
import CloseIcon from 'svg/CloseIcon';
import Button, { ButtonLink, ButtonText } from 'components/Button';
import Modal from 'components/modals/Modal';
import { ALL_EVENTS_LEARN_MORE, type EventTypeLearnMoreItem } from './data';
import type { RichText } from './props';

export type AllEventsLearnMoreModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onGoBack: () => void;
  /** VAIR-only event types start here so the VAIR role check can run first. */
  onStartVairEvent: (href: string, isRatingSession?: boolean) => void;
  /** False while the VAIR role check is still in flight. */
  isVairAccessResolved: boolean;
};

const renderDescription = (description: RichText) =>
  description.map((segment, i) =>
    segment.bold ? (
      <strong key={i}>{segment.text}</strong>
    ) : (
      <React.Fragment key={i}>{segment.text}</React.Fragment>
    ),
  );

const EventLearnMoreSection = ({
  item,
  onClose,
  onStartVairEvent,
  isVairAccessResolved,
}: {
  item: EventTypeLearnMoreItem;
  onClose: () => void;
  onStartVairEvent: (href: string, isRatingSession?: boolean) => void;
  isVairAccessResolved: boolean;
}) => (
  <div className="relative flex flex-col items-center overflow-hidden rounded-2xl px-ds-lg py-ds-xl">
    <div className="mb-ds-md flex items-center justify-center">{item.icon}</div>
    <h3 className="typography-product-subheading mb-ds-sm text-center">{item.title}</h3>
    <p className="typography-product-body mb-ds-lg text-pretty text-center text-color-text-lightmode-primary">
      {renderDescription(item.description)}
    </p>
    {item.isRatingSession ? (
      <Button
        variant="primary"
        size="md"
        onClick={() => onStartVairEvent(item.getStartedHref, true)}
        loading={!isVairAccessResolved}
      >
        Get started
      </Button>
    ) : item.getStartedHref ? (
      <ButtonLink href={item.getStartedHref} variant="primary" size="md" onClick={onClose}>
        Get started
      </ButtonLink>
    ) : (
      <Button variant="primary" size="md" disabled>
        Get started
      </Button>
    )}
  </div>
);

const AllEventsLearnMoreModal: React.FC<AllEventsLearnMoreModalProps> = ({
  isOpen,
  onClose,
  onGoBack,
  onStartVairEvent,
  isVairAccessResolved,
}) => {
  return (
    <Modal isOpen={isOpen} handleClose={onClose} classNameMaxWidth="max-w-[28rem]">
      <div className="flex flex-row justify-end px-ds-lg pt-ds-lg">
        <ButtonText onClick={onGoBack} aria-label="Close">
          <CloseIcon className="h-5 w-5" />
        </ButtonText>
      </div>
      <div className="flex flex-col gap-ds-md px-ds-lg pb-ds-lg">
        {ALL_EVENTS_LEARN_MORE.map((item) => (
          <EventLearnMoreSection
            key={item.title}
            item={item}
            onClose={onClose}
            onStartVairEvent={onStartVairEvent}
            isVairAccessResolved={isVairAccessResolved}
          />
        ))}
        <Button variant="secondary" size="lg" onClick={onGoBack}>
          Go back
        </Button>
      </div>
    </Modal>
  );
};

export default AllEventsLearnMoreModal;
